import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { makeStyles, Grid, CircularProgress, Fab, Button } from '@material-ui/core';
import { AddOutlined } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from './useInfiniteScroll';
import NotificationCard from './NotificationCard';
import MessageCard from '../../UI/MessageCard';
import SearchToolbar from '../../SearchToolbar';
import CSVExport from '../../UI/CSVExport';
import { setFilter, getDocuments, getMoreDocuments } from '../../../actions';

// Tour
import { useTour } from '@reactour/tour';
import { tourSteps } from '../../TourSteps';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 15,
      paddingRight: 15,
      marginTop: 20
    },
    [theme.breakpoints.up('xs')]: {
      marginTop: 20,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up(500)]: {
      marginTop: 40,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 40,
      paddingLeft: 12,
      paddingRight: 12
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: 40,
      paddingLeft: 33,
      paddingRight: 33
    },
    [theme.breakpoints.up('xl')]: {
      marginTop: 40,
      paddingLeft: 80,
      paddingRight: 80
    }
  },
  hideOnSmallScreen: {
    display: 'none',
    [theme.breakpoints.up('xl')]: {
      display: 'flex'
    }
  },
  gridItem: {
    width: '100%',
    marginBottom: 16
  },
  title: {
    color: '#CFD5E5',
    fontSize: '1rem',
    textTransform: 'uppercase',
    padding: '2rem 0'
  },
  topBtns: {
    paddingBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    textTransform: 'uppercase',
    '& button span svg': {
      color: 'white'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    }
  },
  btn: {
    marginRight: '1rem',
    flexShrink: 0
  },
  search: {
    marginTop: '1.5rem'
  }
}));

const NotificationsList = (props) => {
  const { notifications, match } = props;
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const classes = useStyles();
  const { t } = useTranslation();
  const [loading, noMoreData] = useInfiniteScroll(props);

  useEffect(() => {
    const tour = JSON.parse(localStorage.getItem('tour-notification'));
    setSteps(tourSteps.createNewNotifCertificate);
    setCurrentStep(0);
    setIsOpen(tour);
  }, []);

  return (
    <Grid container className={classes.mainContainer} direction='column'>
      <Grid container direction='column' item xs={12} md={10}>
        <Grid container justify='space-between' >
          <Grid item className={classes.topBtns}>
            <Fab
              component={RouterLink}
              to='/notification/new'
              className={classes.btn}
              aria-label='Add'
              color='primary'
            >
              <AddOutlined className='files-newNotification' />
            </Fab>
            <span>
              {t('certificate:list:add')}
            </span>
          </Grid>
          <Grid item className={classes.topBtns}>
            <Button
              component={RouterLink}
              to='/notification/bulk'
              variant={'outlined'}
              className={classes.btnBulk}
              color='primary'>
              {t('certificate:bulkNotification')}
            </Button>
            <CSVExport data={notifications} type={'file'} />
          </Grid>
        </Grid>
        <Grid item md={4}>
          <div className={classes.search}>
            <SearchToolbar visibleFor={[match.path]} />
          </div>
        </Grid>
      </Grid>

      {notifications && notifications.length ? (
        <>
          {loading ? (
            <CircularProgress />
          ) : (
            <Grid container direction='row' item xs={12} md={10}>
              {notifications.map((notification) => (
                <NotificationCard key={notification.id} document={notification} />
              ))}
            </Grid>
          )}
          {noMoreData && (
            <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 8 }}>
              {t('fileListView:noMoreData')}
            </div>
          )}
        </>
      ) : loading ? (
        <CircularProgress />
      ) : (
        <MessageCard
          message={t('certificate:list:notFound')}
        />
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    filter: state.filter,
    notifications: state.documents.documents
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setFilter: (data) => dispatch(setFilter(data)),
    getAllDocuments: () =>
      dispatch(getDocuments()),
    getMoreDocuments: (currentPage, search, docType) =>
      dispatch(getMoreDocuments(currentPage, search, docType))
  };
};

NotificationsList.propTypes = {
  notifications: PropTypes.array.isRequired,
  match: PropTypes.object.isRequired
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(NotificationsList)
);
